import { z } from "zod";

export const REPORT_DATE_BASIS = ["created", "shipped"] as const;

export const ReportProductFilterSchema = z.object({
  productId: z.string().optional(),
  variantId: z.string().optional(),
});
export type ReportProductFilterDTO = z.infer<typeof ReportProductFilterSchema>;

export const ReportDateRangeSchema = ReportProductFilterSchema.extend({
  from: z.string().min(1),
  to: z.string().min(1),
  basis: z.enum(REPORT_DATE_BASIS).optional().default("created"),
});
export type ReportDateRangeDTO = z.infer<typeof ReportDateRangeSchema>;

export const PRODUCT_PERFORMANCE_SORT = ["best", "worst"] as const;

export const ProductPerformanceQuerySchema = ReportDateRangeSchema.extend({
  limit: z.coerce.number().int().min(1).max(50).optional().default(10),
  sort: z.enum(PRODUCT_PERFORMANCE_SORT).optional().default("best"),
});
export type ProductPerformanceQueryDTO = z.infer<
  typeof ProductPerformanceQuerySchema
>;

export const AgingStockQuerySchema = ReportProductFilterSchema.extend({
  minDays: z.coerce.number().int().min(1).max(3650).optional().default(30),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type AgingStockQueryDTO = z.infer<typeof AgingStockQuerySchema>;

export const DeadStockQuerySchema = ReportProductFilterSchema.extend({
  lookbackDays: z.coerce.number().int().min(1).max(3650).optional(),
  minDays: z.coerce.number().int().min(1).max(3650).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
});
export type DeadStockQueryDTO = z.infer<typeof DeadStockQuerySchema>;

export const SALES_FREQUENCY_SORT = [
  "orderCount",
  "ordersPerWeek",
  "quantity",
] as const;

export const SalesFrequencyQuerySchema = ReportDateRangeSchema.extend({
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  sort: z.enum(SALES_FREQUENCY_SORT).optional().default("orderCount"),
});
export type SalesFrequencyQueryDTO = z.infer<typeof SalesFrequencyQuerySchema>;

export const STOCK_MOVEMENT_REASONS = [
  "order_paid",
  "order_cancelled",
  "order_returned",
  "manual_adjust",
  "goods_receipt",
  "goods_receipt_void",
  "import_order_receive",
] as const;

export const StockValuationQuerySchema = ReportProductFilterSchema.extend({
  limit: z.coerce.number().int().min(1).max(200).optional().default(20),
});
export type StockValuationQueryDTO = z.infer<typeof StockValuationQuerySchema>;

export const StockMovementsQuerySchema = ReportProductFilterSchema.extend({
  from: z.string().min(1),
  to: z.string().min(1),
  reason: z.enum(STOCK_MOVEMENT_REASONS).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type StockMovementsQueryDTO = z.infer<typeof StockMovementsQuerySchema>;

export const PROFIT_DATE_BASIS = ["paid_at", "created_at"] as const;

export const ProfitReportQuerySchema = ReportProductFilterSchema.extend({
  from: z.string().min(1),
  to: z.string().min(1),
  basis: z.enum(PROFIT_DATE_BASIS).optional().default("paid_at"),
  platform: z.string().optional(),
  campaignName: z.string().optional(),
  mappedProductId: z.string().optional(),
  mappedCategoryId: z.string().optional(),
});
export type ProfitReportQueryDTO = z.infer<typeof ProfitReportQuerySchema>;

export const ProfitByProductQuerySchema = ProfitReportQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(50).optional().default(10),
});
export type ProfitByProductQueryDTO = z.infer<
  typeof ProfitByProductQuerySchema
>;

export const ProfitCampaignSummaryQuerySchema = ProfitReportQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type ProfitCampaignSummaryQueryDTO = z.infer<
  typeof ProfitCampaignSummaryQuerySchema
>;

export const MarketingRoiQuerySchema = ProfitCampaignSummaryQuerySchema;
export type MarketingRoiQueryDTO = z.infer<typeof MarketingRoiQuerySchema>;

export const ShippingPerformanceQuerySchema = ReportDateRangeSchema.extend({
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
export type ShippingPerformanceQueryDTO = z.infer<
  typeof ShippingPerformanceQuerySchema
>;

export const DeadStockSettingsSchema = z.object({
  lookbackDays: z.number().int().min(1).max(3650),
  minDays: z.number().int().min(1).max(3650),
});
export type DeadStockSettingsResponseDTO = z.infer<
  typeof DeadStockSettingsSchema
>;

export const UpdateDeadStockSettingsSchema = z.object({
  lookbackDays: z.coerce.number().int().min(1).max(3650).optional(),
  minDays: z.coerce.number().int().min(1).max(3650).optional(),
});
export type UpdateDeadStockSettingsDTO = z.infer<
  typeof UpdateDeadStockSettingsSchema
>;

export const CustomerReportQuerySchema = ReportDateRangeSchema.extend({
  province: z.string().optional(),
  district: z.string().optional(),
});
export type CustomerReportQueryDTO = z.infer<typeof CustomerReportQuerySchema>;

export const CUSTOMER_TOP_SORT = ["revenue", "orders", "lastOrderAt"] as const;

export const CustomerTopQuerySchema = CustomerReportQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  sort: z.enum(CUSTOMER_TOP_SORT).optional().default("revenue"),
});
export type CustomerTopQueryDTO = z.infer<typeof CustomerTopQuerySchema>;

export const CustomerGeoDistrictQuerySchema = CustomerReportQuerySchema.extend({
  province: z.string().min(1),
});
export type CustomerGeoDistrictQueryDTO = z.infer<
  typeof CustomerGeoDistrictQuerySchema
>;

export const CUSTOMER_WATCHLIST_KINDS = [
  "cod_returns",
  "inactive",
  "high_new",
] as const;

export const CUSTOMER_COD_SCOPES = ["all_time", "period"] as const;

export const CUSTOMER_EXPORT_SECTIONS = [
  "overview",
  "top",
  "geo",
  "watch",
] as const;

export const CustomerWatchlistQuerySchema = CustomerReportQuerySchema.extend({
  kind: z.enum(CUSTOMER_WATCHLIST_KINDS).default("cod_returns"),
  minCodReturns: z.coerce.number().int().min(1).max(20).optional().default(1),
  codScope: z.enum(CUSTOMER_COD_SCOPES).optional().default("all_time"),
  inactiveDays: z.coerce.number().int().min(1).max(3650).optional().default(90),
  highNewThresholdLak: z.coerce
    .number()
    .int()
    .min(1)
    .optional()
    .default(500_000),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
export type CustomerWatchlistQueryDTO = z.infer<
  typeof CustomerWatchlistQuerySchema
>;

export const CustomerExportQuerySchema = CustomerReportQuerySchema.extend({
  exportSection: z.enum(CUSTOMER_EXPORT_SECTIONS).optional().default("top"),
  watchKind: z.enum(CUSTOMER_WATCHLIST_KINDS).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional().default(500),
});
export type CustomerExportQueryDTO = z.infer<typeof CustomerExportQuerySchema>;

export const CustomerProductAffinityQuerySchema =
  CustomerReportQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(50).optional().default(15),
  });
export type CustomerProductAffinityQueryDTO = z.infer<
  typeof CustomerProductAffinityQuerySchema
>;

export const CustomerCohortsQuerySchema = z.object({
  months: z.coerce.number().int().min(3).max(12).optional().default(6),
  maxOffset: z.coerce.number().int().min(1).max(12).optional().default(6),
  province: z.string().optional(),
  district: z.string().optional(),
});
export type CustomerCohortsQueryDTO = z.infer<typeof CustomerCohortsQuerySchema>;
